import { Reminder } from '../../interfaces/reminder.interface';
import { DocumentType, ExternalArchive } from "../../interfaces/project.interface";
import { HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { RequestBody } from "../../interfaces/admin.interface";
import { SettingsService } from "../../settings/settings.service";
import { LoggingService } from "../log.service";
import { RestfulService } from "../restful.service";
import * as ENDPOINT from "../../../shared/constant/endPoint.js";
import { getEndPointWithParams } from "../../../../assets/js/util.js";
import { Project, ProjectActions, ProjectInfo } from "../../interfaces/project.interface";
import { UserInfo } from '../../interfaces/user.interface';
import { UtilsService } from '../utils.service';

@Injectable({
  providedIn: "root",
})
export class ProjectService {
  typeAction: ProjectActions;

  projectSaved: ProjectInfo = null;
  idProject: number = null;
  requestBody: RequestBody = null;
  comingFromDashBoardPage: boolean = false;

  constructor(
    private logger: LoggingService,
    private settings: SettingsService,
    private restful: RestfulService,
    private utils: UtilsService
  ) { }

  setTypeAction(typeAction: ProjectActions): void {
    this.typeAction = typeAction;
  }

  getTypeAction(): ProjectActions {
    return this.typeAction;
  }

  setComingFromDashboardPage(val: boolean): void {
    this.comingFromDashBoardPage = val;
  }

  isComingFromDashboardPage(): boolean {
    return this.comingFromDashBoardPage;
  }

  setIdProject(idProject: number): void {
    this.idProject = idProject;
  }

  getIdProject(): number {
    return this.idProject;
  }

  saveProjectInfo(project: ProjectInfo): void {
    this.projectSaved = project;
  }

  getSavedProject(): ProjectInfo {
    return this.projectSaved;
  }

  setFilters(reqBody: RequestBody): void {
    this.requestBody = reqBody;
  }

  getFilters(): RequestBody {
    return this.requestBody;
  }

  getProjectWorkflow(idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_projectWorkflow, idProject);
    return this.get(endPoint);
  }

  getProjectDocumentsList(reqBody: any, idProject: number, documentType: string): Observable<any> {
    reqBody.paging = false;
    let tempEndPoint = documentType === DocumentType.WORKFLOW_DOCS ? ENDPOINT.project_projectDocuments : ENDPOINT.project_safetyDocuments;
    const endPoint = getEndPointWithParams(tempEndPoint, idProject);
    return this.post(endPoint, reqBody);
  }

  getProjectReminderList(reqBody: RequestBody, idProject: number, isDashboard: boolean = false): Observable<any> {
    let endPoint = getEndPointWithParams(ENDPOINT.project_projectReminder, idProject);
    if (isDashboard) {
      endPoint = ENDPOINT.project_reminder_all;
    }
    return this.post(endPoint, reqBody);
  }

  getProjectActivity(idActivity: number, idProject: number, idProjectActivity: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_projectActivity, idActivity, idProject, idProjectActivity ? idProjectActivity : "");
    return this.get(endPoint);
  }

  updateProjectActivity(reqBody: any): Observable<any> {
    const endPoint = ENDPOINT.project_updateProjectActivity;
    return this.post(endPoint, reqBody);
  }

  downloadDocument(idFile: number, fileName: string): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.file_download, idFile);
    return this.get_downloadFile(endPoint, fileName);
  }

  restoreDocument(idDocumentToRestore: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_restoreDocument, idDocumentToRestore);
    return this.put(endPoint, null);
  }

  deleteExtraDocument(idDocumentToDelete: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_deleteExtraDocument, idDocumentToDelete);
    return this.delete(endPoint);
  }

  getProjectGeneralInformation(idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_general_info, idProject);
    return this.get(endPoint);
  }

  getProjectInformation(idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_project_info, idProject);
    return this.get(endPoint);
  }

  loadExternalArchive(reqBody: RequestBody, idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_external_archive, idProject);
    return this.post(endPoint, reqBody);
  }

  createExternalArchive(externalArchive: ExternalArchive, idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_external_archive_create, idProject);
    return this.post(endPoint, externalArchive);
  }

  updateExternalArchive(externalArchive: ExternalArchive, idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_external_archive_update, idProject);
    return this.post(endPoint, externalArchive);
  }

  deleteExternalArchive(externalArchive: ExternalArchive, idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_external_archive_delete, idProject, externalArchive.id);
    return this.delete(endPoint);
  }

  getProjectList(reqBody: RequestBody): Observable<any> {
    const endPoint = ENDPOINT.project_all;
    return this.post(endPoint, reqBody);
  }

  getProjectInfo(idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_project_info, idProject);
    return this.get(endPoint);
  }

  createProject(project: Project): Observable<any> {
    const endPoint = ENDPOINT.project_create;
    return this.post(endPoint, project);
  }

  updateProject(project: Project): Observable<any> {
    const endPoint = ENDPOINT.project_update;
    return this.post(endPoint, project);
  }

  deleteProject(idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_delete, idProject);
    return this.delete(endPoint);
  }

  getAssociatedUsers(idProject: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_associatedUsers, idProject);
    return this.get(endPoint);
  }

  associateUsers(idProject: number, userList: UserInfo[]): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_associateUsers, idProject);
    return this.post(endPoint, userList);
  }

  disassociateUser(idProject: number, idUser: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_disassociateUser, idProject, idUser);
    return this.delete(endPoint);
  }

  createReminder(reminder: Reminder): Observable<any> {
    const endPoint = ENDPOINT.project_createReminder;
    return this.post(endPoint, reminder);
  }

  updateReminder(reminder: Reminder): Observable<any> {
    const endPoint = ENDPOINT.project_updateReminder;
    return this.post(endPoint, reminder);
  }

  toggleStateReminder(idReminder: number): Observable<any> {
    const endPoint = getEndPointWithParams(ENDPOINT.project_toggleStateReminder, idReminder);
    return this.put(endPoint, null);
  }

  /* RESTFUL METHOD */

  post(endPoint: string, reqBody: any): Observable<any> {
    return Observable.create((observer) => {
      this.restful.post(endPoint, reqBody).subscribe((response) => {
        this.responseHandler(response, observer);
      });
    });
  }

  put(endPoint: string, reqBody: any): Observable<any> {
    return Observable.create((observer) => {
      this.restful.put(endPoint, reqBody).subscribe((response) => {
        this.responseHandler(response, observer);
      });
    });
  }

  delete(endPoint: string): Observable<any> {
    return Observable.create((observer) => {
      this.restful.delete(endPoint).subscribe((response) => {
        this.responseHandler(response, observer);
      });
    });
  }

  get(endPoint: string): Observable<any> {
    return Observable.create((observer) => {
      this.restful.get(endPoint).subscribe((response) => {
        this.responseHandler(response, observer);
      });
    });
  }

  get_downloadFile(endPoint: string, fileName: any): Observable<any> {
    return Observable.create((observer) => {
      this.restful.get_downloadFile(endPoint).subscribe(
        (response) => {
          this.responseHandler_downloadFile(response, fileName, observer);
        },
        (error) => {
          observer.error("download file error");
        }
      );
    });
  }

  responseHandler(response, observer: any): void {
    const outcome = response["outcome"];
    if (outcome.success === true) {
      this.logger.log("Service:", "SUCCESS", 200);
      observer.next(response);
    } else {
      this.logger.log("Service:", "FAILURE", 200);
      outcome.message = this.utils.manageErrorMsg(outcome);
      observer.error(outcome);
    }
  }

  responseHandler_downloadFile(response, fileName: string, observer: any): void {
    observer.next(response);
  }
}
